import React, { useCallback, useEffect, useState } from "react";
import { Table, Tag, Button, Tooltip, message, Modal, Empty } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  StopOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { fileShareService } from "@iso/services/fileShare.service";
import {
  getFileIcon,
  getFileName,
  formatFileSize,
  formatDateTimeAu,
} from "@iso/components/FileBrowser/fileUtils";

const userLabel = (user) => {
  if (!user) return "Unknown";
  const name = `${user.name || ""} ${user.surname || ""}`.trim();
  return name || user.email || `User #${user.id}`;
};

const StatusTag = ({ status }) =>
  status === "revoked" ? (
    <Tag color="default">Revoked</Tag>
  ) : (
    <Tag color="green">Active</Tag>
  );

const SharedFilesTab = ({ direction = "received", onPreview }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fileShareService.list({ direction });
      if (res && (res.success || res.code === "0000")) {
        setShares(Array.isArray(res.shares) ? res.shares : []);
      } else {
        setShares([]);
        message.error(res?.message || "Failed to load shares");
      }
    } catch (err) {
      setShares([]);
      message.error("Failed to load shares");
    } finally {
      setLoading(false);
    }
  }, [direction]);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const handleOpen = useCallback(
    async (share) => {
      setBusyId(share.id);
      try {
        const res = await fileShareService.download(share.id);
        if (res && (res.success || res.code === "0000") && res.full_path) {
          const merged = { ...(share.attachment || {}), full_path: res.full_path };
          if (onPreview) {
            onPreview(merged);
          } else {
            window.open(res.full_path, "_blank", "noopener,noreferrer");
          }
        } else {
          message.error(res?.message || "File no longer available");
        }
      } catch (err) {
        message.error("Failed to open file");
      } finally {
        setBusyId(null);
      }
    },
    [onPreview]
  );

  const handleDownload = useCallback(async (share) => {
    setBusyId(share.id);
    try {
      const res = await fileShareService.download(share.id);
      if (!res || !res.full_path) {
        message.error(res?.message || "File no longer available");
        return;
      }
      const link = document.createElement("a");
      link.href = res.full_path;
      const name = getFileName(share.attachment || {});
      if (name) link.download = name;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      message.error("Failed to download file");
    } finally {
      setBusyId(null);
    }
  }, []);

  const handleRevoke = useCallback(
    (share) => {
      Modal.confirm({
        title: "Revoke share?",
        content: `${userLabel(share.recipient)} will no longer be able to access "${getFileName(share.attachment || {})}".`,
        okText: "Revoke",
        okType: "danger",
        onOk: async () => {
          setBusyId(share.id);
          try {
            const res = await fileShareService.revoke(share.id);
            if (res && (res.success || res.code === "0000")) {
              message.success("Share revoked");
              fetchShares();
            } else {
              message.error(res?.message || "Failed to revoke share");
            }
          } catch (err) {
            message.error("Failed to revoke share");
          } finally {
            setBusyId(null);
          }
        },
      });
    },
    [fetchShares]
  );

  const isSent = direction === "sent";

  const columns = [
    {
      title: "File",
      key: "file",
      ellipsis: true,
      render: (_, share) => {
        const a = share.attachment || {};
        const name = getFileName(a);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>{getFileIcon(a.type)}</span>
            <span title={name}>{name || `Attachment #${share.attachment_id}`}</span>
          </div>
        );
      },
    },
    {
      title: isSent ? "Recipient" : "From",
      key: "user",
      width: 200,
      render: (_, share) => userLabel(isSent ? share.recipient : share.sender),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      render: (msg) =>
        msg ? (
          <Tooltip title={msg}>
            <span style={{ color: "#666" }}>{msg}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Size",
      key: "size",
      width: 90,
      render: (_, share) => formatFileSize((share.attachment || {}).size_bytes),
    },
    {
      title: isSent ? "Sent" : "Received",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (raw) => formatDateTimeAu(raw),
    },
  ];

  if (isSent) {
    columns.push({
      title: "Status",
      key: "status",
      width: 110,
      render: (_, share) => <StatusTag status={share.status} />,
    });
  }

  columns.push({
    title: "",
    key: "actions",
    width: isSent ? 140 : 120,
    align: "right",
    render: (_, share) => {
      const a = share.attachment || {};
      const disabled = busyId === share.id;
      return (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {!isSent && (
            <Tooltip title="Preview">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                disabled={disabled || !a.id}
                onClick={() => handleOpen(share)}
              />
            </Tooltip>
          )}
          <Tooltip title="Download">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              disabled={disabled || !a.id || share.status === "revoked"}
              onClick={() => handleDownload(share)}
            />
          </Tooltip>
          {isSent && share.status === "active" && (
            <Tooltip title="Revoke share">
              <Button
                type="text"
                size="small"
                danger
                icon={<StopOutlined />}
                disabled={disabled}
                onClick={() => handleRevoke(share)}
              />
            </Tooltip>
          )}
        </div>
      );
    },
  });

  if (!loading && shares.length === 0) {
    return (
      <Empty
        image={<ShareAltOutlined style={{ fontSize: 48, color: "#ccc" }} />}
        description={
          isSent
            ? "You haven't shared any files yet"
            : "No files have been shared with you"
        }
      />
    );
  }

  return (
    <Table
      rowKey="id"
      loading={loading}
      columns={columns}
      dataSource={shares}
      pagination={{ pageSize: 15, showSizeChanger: false }}
      size="middle"
    />
  );
};

export default SharedFilesTab;
